import { useState, useEffect } from "react";
import { Button, TextInput, StaticSelect, CustomModal, SignupInterestCheckbox, FormError, showToast } from "../shared";
import { extractBackendErrorMessage, toIsoDate, toUsDate } from "../../utils/utils";
import { useCreateTask } from "../../lib/react-query/tasks";
import { I18nProvider } from "@react-aria/i18n";
import { DatePicker } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { STATUS_OPTIONS } from "../../constants/statuses";

const CreateTaskModal = ({ isOpen, onClose, projectId, tagOptions = [] }) => {
  const { mutateAsync: createTask, isLoading: creating } = useCreateTask();
  const [state, setState] = useState({ loading: false, error: null });
  const [form, setForm] = useState({
    title: "",
    description: "",
    status: "PENDING",
    priority: "MEDIUM",
    start_date: "",
    due_date: "",
    tags: [],
  });
  const startIso = toIsoDate(form.start_date);
  const dueIso = toIsoDate(form.due_date);
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setState({ loading: false, error: null });
      setForm({ title: "", description: "", status: "PENDING", priority: "MEDIUM", start_date: "", due_date: "", tags: [] });
      setNewTag("");
    }
  }, [isOpen]);

  const parseError = (err) => extractBackendErrorMessage(err, "Failed to create task");

  const handleCreate = async () => {
    if (!form.title?.trim()) {
      showToast("Title is required", "error", { position: "top-center" });
      return;
    }
    try {
      setState({ loading: true, error: null });
      const payload = {
        title: form.title,
        description: form.description,
        status: form.status,
        priority: form.priority,
        start_date: form.start_date,
        due_date: form.due_date,
        tags: form.tags,
      };
      await createTask({ projectId, payload });
      onClose?.();
      setState({ loading: false, error: null });
      setForm({ title: "", description: "", status: "PENDING", priority: "MEDIUM", start_date: "", due_date: "", tags: [] });
    } catch (err) {
      const message = parseError(err);
      setState({ loading: false, error: message });
    }
  };

  return (
    <CustomModal
      title={"Create New Task"}
      open={isOpen}
      close={onClose}
      primaryButtonText={state.loading || creating ? "Creating..." : "Create"}
      primaryButtonClick={handleCreate}
      primaryButtonLoading={state.loading || creating}
      primaryButtonDisabled={state.loading || creating || !form.title?.trim()}
      secondaryButtonText={"Cancel"}
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-4">
        <TextInput label="Title" type="text" name="title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))} required />
        <TextInput label="Description" type="text" name="description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))} />
        <StaticSelect
          options={STATUS_OPTIONS}
          label="Status"
          name="status"
          value={form.status}
          onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
        />
        <StaticSelect
          options={[{ id: "LOW", name: "Low" }, { id: "MEDIUM", name: "Medium" }, { id: "HIGH", name: "High" }]}
          label="Priority"
          name="priority"
          value={form.priority}
          onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
        />

        <I18nProvider locale="en-US">
          <DatePicker
            label="Start Date"
            disableAnimation
            granularity="day"
            value={startIso ? parseDate(startIso) : null}
            onChange={(val) => setForm((p) => ({ ...p, start_date: val ? toUsDate(val.toString()) : "" }))}
          />
          <DatePicker
            label="Due Date"
            disableAnimation
            granularity="day"
            value={dueIso ? parseDate(dueIso) : null}
            onChange={(val) => setForm((p) => ({ ...p, due_date: val ? toUsDate(val.toString()) : "" }))}
          />
        </I18nProvider>
        <div>
          <p className="font-medium text-sm text-grey pb-1">Tags</p>
          <div className="flex items-end gap-2">
            <TextInput label="Add a tag" type="text" name="new_tag" value={newTag} onChange={(e) => setNewTag(e.target.value)} />
            <Button onClick={() => {
              const v = newTag.trim();
              if (!v) return;
              setForm((p) => ({ ...p, tags: Array.from(new Set([...(p.tags || []), v])) }));
              setNewTag("");
            }} variant="outlined" color="secondary" className="px-3 py-1 shrink-0">Add</Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {Array.from(new Set([...(tagOptions || []), ...(form.tags || [])])).map((tag) => {
              const checked = form.tags.includes(tag);
              return (
                <SignupInterestCheckbox key={tag} name={tag} value={checked} onChange={() => {
                  setForm((p) => ({ ...p, tags: checked ? p.tags.filter((t) => t !== tag) : [...p.tags, tag] }));
                }}>
                  {tag}
                </SignupInterestCheckbox>
              );
            })}
          </div>
        </div>
        <FormError message={state.error} />
      </div>
    </CustomModal>
  );
};

export default CreateTaskModal;
